import React, { useState, useRef } from 'react';
import Mindset from './Mindset';
import ProcessWizard from './ProcessWizard';
import { Wand2 } from 'lucide-react';
import Mixpanel from '../utils/mixpanel';
import axios from 'axios';
import API_URL from '../config';
import logger from '../utils/logger';


const MindsetPage = () => {
  const [isProcessWizardOpen, setIsProcessWizardOpen] = useState(false);
  const mindsetRef = useRef(null);
  
  // Track page view when component mounts
  React.useEffect(() => {
    Mixpanel.trackEnhancedPageView('/mindset', 'Mindset', {
      feature: 'Mindset',
      action: 'Opened'
    });

    // Check if mindset data is empty
    const checkMindsetData = async () => {
      try {
        // Check localStorage first
        const hasContent = localStorage.getItem('mindsetDataHasContent') === 'true';
        if (hasContent) {
          // If we have content, mark the task as completed
          markMindsetCompleted();
          return; // Data exists, no need to open wizard
        }

        // Check if we have a token
        const token = localStorage.getItem('token');
        if (!token) {
          return; // No token, can't check API
        }

        // Try to get data from API
        const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
        try {
          const response = await axios.get(`${baseUrl}/api/mindset`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          logger.debug('Mindset data from API:', JSON.stringify(response.data, null, 2));
          
          // Check if API data has any content - check ALL fields
          const apiHasContent = response.data.oneWordFocus || 
                             (response.data.keysToKeepItSimple && response.data.keysToKeepItSimple.length > 0) ||
                             (response.data.affirmations && response.data.affirmations.length > 0) ||
                             (response.data.selfTalk && response.data.selfTalk.length > 0) ||
                             (response.data.bodyLanguage && response.data.bodyLanguage.length > 0) ||
                             (response.data.beliefs && response.data.beliefs.length > 0);
          
          logger.debug('API has content:', apiHasContent);
          
          if (apiHasContent) {
            // If API has content, save it to localStorage and mark the flag
            localStorage.setItem('mindsetData', JSON.stringify(response.data));
            localStorage.setItem('mindsetDataHasContent', 'true');
            logger.debug('Saved API mindset data to localStorage with content flag');
            
            // Mark the task as completed
            markMindsetCompleted();
          } else {
            // No content found, open the wizard
            setIsProcessWizardOpen(true);
          }
        } catch (error) {
          logger.error('Error checking mindset data:', error);
          // If API call fails, open the wizard to ensure user can create content
          setIsProcessWizardOpen(true);
        }
      } catch (error) {
        logger.error('Error in checkMindsetData:', error);
      }
    };

    checkMindsetData();
  }, []);

  // Function to mark mindset task as completed
  const markMindsetCompleted = () => {
    // Mark mindset as completed in localStorage
    localStorage.setItem('mindsetCompleted', 'true');
    localStorage.setItem('mindsetDataHasContent', 'true');
    
    // Update the server if possible
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // API_URL already includes '/api', so we need to use it correctly
        axios.post(`${API_URL}/users/starter-task`, 
          { taskId: 'mindsetCompleted', completed: true },
          { headers: { Authorization: `Bearer ${token}` }}
        ).catch(error => {
          logger.error('Error updating mindset completion status on server:', error);
        });
      } catch (error) {
        logger.error('Error updating mindset completion status:', error);
      }
    }
  };

  const handleProcessWizardComplete = (processData) => {
    logger.debug('MindsetPage: Process Wizard completed with data:', JSON.stringify(processData, null, 2));
    
    // Close the wizard
    setIsProcessWizardOpen(false);
    
    // Pass the data to the Mindset component if the ref is available
    if (mindsetRef.current && mindsetRef.current.handleProcessWizardComplete) {
      logger.debug('MindsetPage: Passing data to Mindset component');
      mindsetRef.current.handleProcessWizardComplete(processData);
    } else {
      logger.error('MindsetPage: Could not access Mindset component reference');
    }
    
    // Mark mindset as completed
    markMindsetCompleted();
    
    // Track completion in analytics
    Mixpanel.track('Process Wizard Completed', {
      feature: 'Mindset',
      action: 'Completed Process Wizard'
    });
  };

  return (
    <>
      {/* Mindset Content */}
      <Mindset ref={mindsetRef} />

      {/* Process Wizard Modal */}
      <ProcessWizard 
        isOpen={isProcessWizardOpen} 
        onClose={() => setIsProcessWizardOpen(false)}
        onComplete={handleProcessWizardComplete}
      />
    </>
  );
};

export default MindsetPage;
